import { useContext, useEffect, useRef } from 'react';
import { Text } from '@fluentui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { EditItem } from '@moko/ui';
import AppContext from '../AppContext';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  createTimeService,
  getTimeService,
  type ITimeServiceBody,
  updateTimeService,
} from '../../services/pricingService';
import { timeServicesPath } from './pricingSettings';

const text = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const checked = (value: unknown) => value === true || value === 'true';

const toBody = (item: Record<string, unknown>): ITimeServiceBody | string => {
  const code = text(item.code);
  const name = text(item.name);
  if (!code || !name) return 'Code and name are required.';
  const rawOrder = item.sortOrder;
  const sortOrder =
    typeof rawOrder === 'number' && Number.isInteger(rawOrder)
      ? rawOrder
      : null;
  return {
    code,
    name,
    sortOrder,
    isActive: checked(item.isActive),
  };
};

const TimeServiceForm = () => {
  const isSaved = useRef(false);
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const serviceId = Number.parseInt(query.get('id') ?? '');
  const isEdit = !Number.isNaN(serviceId) && serviceId > 0;
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const context = useContext(AppContext);
  const title = isEdit ? 'Edit time service' : 'Add time service';

  useEffect(() => {
    context.setSelectedTab('Global settings');
  }, []);

  const save = async (
    abortController: AbortController,
    id: number | undefined,
    item: Record<string, unknown>
  ) => {
    const body = toBody(item);
    if (typeof body === 'string') {
      setErrorMessage(body);
      throw new Error(body);
    }
    if (id == null) {
      await createTimeService(abortController, body);
      setSuccessMessage(`Time service ${body.code} created.`);
    } else {
      await updateTimeService(abortController, id, body);
      setSuccessMessage(`Time service ${body.code} updated.`);
    }
    navigate(timeServicesPath);
  };

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <Text variant="xLarge">{title}</Text>
      <EditItem
        getAction={async (abortController, id) =>
          getTimeService(abortController, id)
        }
        newAction={(abortController, item) =>
          save(abortController, undefined, item)
        }
        editAction={(abortController, id, item) =>
          save(abortController, id, item)
        }
        isSaved={isSaved}
        back={timeServicesPath}
        confirmOnCancel={false}
        onCancel={() => navigate(timeServicesPath)}
        defaultValues={isEdit ? undefined : JSON.stringify({ isActive: true })}
        metadata={{
          fields: [
            {
              name: 'code',
              fieldType: 'String',
              label: 'Code',
              mandatory: true,
            },
            {
              name: 'name',
              fieldType: 'String',
              label: 'Name',
              mandatory: true,
            },
            { name: 'isActive', fieldType: 'Checkbox', label: 'Active' },
          ],
          lookups: [],
          validations: [
            {
              field: 'code',
              rules: [{ rule: 'NE', errorMessage: 'Code is required.' }],
            },
            {
              field: 'name',
              rules: [{ rule: 'NE', errorMessage: 'Name is required.' }],
            },
          ],
          interactions: [],
        }}
      />
    </div>
  );
};

export default TimeServiceForm;
